import React, { useState, useEffect } from 'react';
import Navigation from './Navigation';
import Footer from './Footer';

const LegalPage = () => {
  const [activeSection, setActiveSection] = useState('privacy-policy');

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  useEffect(() => {
    // Jump to the section named in the URL hash, e.g. /legal#terms-conditions
    const hash = window.location.hash.slice(1);
    if (hash) scrollToSection(hash);
  }, []);

  useEffect(() => {
    // Update active section based on scroll position
    const handleScroll = () => {
      const sections = ['privacy-policy', 'terms-conditions', 'refund-policy', 'shipping-policy', 'cancellation-policy'];
      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="bg-white min-h-screen">
      <Navigation />
      
      {/* Page Header */}
      <div className="pt-20 pb-8 bg-gradient-to-r from-red-50 to-orange-50">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 text-center mb-4 mt-6">
            Legal Policies
          </h1>
          <p className="text-lg text-gray-600 text-center">
            Important information about our services, privacy, and policies
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:w-1/4">
            <div className="sticky top-24 bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Navigation</h2>
              <nav className="space-y-2">
                {[
                  { id: 'privacy-policy', label: 'Privacy Policy' },
                  { id: 'terms-conditions', label: 'Terms & Conditions' },
                  { id: 'refund-policy', label: 'Refund Policy' },
                  { id: 'shipping-policy', label: 'Shipping Policy' },
                  { id: 'cancellation-policy', label: 'Cancellation Policy' }
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full text-left px-3 py-2 rounded-md transition-colors !bg-transparent !border-transparent ${
                      activeSection === item.id
                        ? '!bg-red-100 !text-red-700 font-medium !border-red-200'
                        : '!text-gray-600 hover:!bg-gray-50 hover:!text-gray-900'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:w-3/4">
            <div className="bg-white rounded-lg shadow-md">
              {/* Privacy Policy Section */}
              <section id="privacy-policy" className="p-8 border-b border-gray-200 !opacity-100">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                  <span className="w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-sm font-bold mr-3">1</span>
                  Privacy Policy
                </h2>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-600 mb-4">
                    Fijoli collects users' contact details for marketing, broadcasting, and communication purposes. Updates, articles, and other information will be published on the Fijoli website, and notifications may be shared through the contact details or WhatsApp number provided by the user.
                  </p>
                  <p className="text-gray-600 mb-4">
                    Fijoli will not be responsible for any hacking, spamming, or other breaches occurring while users log in to access the book through the website.
                  </p>
                </div>
              </section>

              {/* Terms & Conditions Section */}
              <section id="terms-conditions" className="p-8 border-b border-gray-200 !opacity-100">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                  <span className="w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-sm font-bold mr-3">2</span>
                  Terms & Conditions
                </h2>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-600 mb-4">
                    The articles and information in the book have been reviewed and acknowledged by qualified professionals such as doctors, dieticians, or sports experts.
                  </p>
                  <p className="text-gray-600 mb-4">
                    Readers are advised to follow the guidance of their own trainers, doctors, or other certified professionals before beginning any diet or workout program. All activities should be carried out under proper supervision.
                  </p>
                  <p className="text-gray-600 mb-4">
                    Fijoli will not be responsible or liable for any negative outcomes, injuries, loss of life, or other consequences arising from the use of the content in the book.
                  </p>
                  <p className="text-gray-600 mb-4">
                    The Web Access version of the book cannot be downloaded or screenshotted. Copying or reproducing any content is considered infringement and may result in legal action.
                  </p>
                  <p className="text-gray-600 mb-4">
                    Fijoli reserves the right to restrict or revoke access, or to modify these Terms and Conditions at any time. Users are required to comply with the updated terms.
                  </p>
                  <p className="text-gray-600 mb-4">
                    We collect WhatsApp numbers and other personal details to contact users in the future and to share updates, articles, and new information.
                  </p>
                  <p className="text-gray-600 mb-4">
                    All buyers of the hard copy will automatically receive access to the Web Access version of the book.
                  </p>
                  <p className="text-gray-600 mb-4">
                    Fijoli reserves the right to modify the design, content, or the entire website at its sole discretion.
                  </p>
                </div>
              </section>

              {/* Refund Policy Section */}
              <section id="refund-policy" className="p-8 border-b border-gray-200 !opacity-100">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                  <span className="w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-sm font-bold mr-3">3</span>
                  Refund Policy
                </h2>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-600 mb-4">
                    Once payment has been made for the book, no cancellations or refunds will be entertained for any reason.
                  </p>
                  <p className="text-gray-600 mb-4">
                    For support, please contact us at fitnessjourney@fijoli.com or via WhatsApp at +91 9008023230.
                  </p>
                  <p className="text-gray-600 mb-4">
                    Fijoli will not be liable for any form of compensation.
                  </p>
                  <p className="text-gray-600 mb-4">
                    If payment has been made but access is restricted, please contact support. In cases where the payment has been deducted from the user's account but not received by Fijoli, the user must contact their respective bank to resolve the transaction issue.
                  </p>
                </div>
              </section>

              {/* Shipping Policy Section */}
              <section id="shipping-policy" className="p-8 border-b border-gray-200 !opacity-100">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                  <span className="w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-sm font-bold mr-3">4</span>
                  Shipping Policy
                </h2>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-600 mb-4">
                    Hard copies of the book will be couriered within 5–7 working days. Free shipping is available only within India. Additional charges will apply for deliveries outside India.
                  </p>
                  <p className="text-gray-600 mb-4">
                    Users must provide accurate delivery addresses and contact details to ensure timely delivery. While Fijoli will dispatch through trusted couriers, it will not be responsible for any damages or delays caused during transit.
                  </p>
                </div>
              </section>

              {/* Cancellation Policy Section */}
              <section id="cancellation-policy" className="p-8 !opacity-100">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                  <span className="w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-sm font-bold mr-3">5</span>
                  Cancellation Policy
                </h2>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-600 mb-4">
                    Once payment has been made for the book, no cancellations will be entertained for any reason.
                  </p>
                  <p className="text-gray-600 mb-4">
                    For support regarding cancellations, please contact us at fitnessjourney@fijoli.com or via WhatsApp at +91 9008023230.
                  </p>
                </div>
              </section>
            </div>

            {/* Contact Information */}
            <div className="mt-8 bg-gray-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Questions About Our Policies?</h3>
              <p className="text-gray-600 mb-4">
                If you have any questions about our policies, please don't hesitate to contact us.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="mailto:fitnessjourney@fijoli.com"
                  className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Email Us
                </a>
                <a
                  href="https://wa.me/+919008023230"
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  WhatsApp Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LegalPage;
